import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { IDENTITY_TOGGLE_COPY, POINT_MARKER_TYPES, WIND_CODES, markerTypeSelectValue } from '../utils/pointIdentityPayload';
import { shouldGateFeedEnable } from '../../pro/entitlements';
import { ProPanel } from '../../pro/ProPanel';
import { YButton } from '../../../ui/YButton';
import { YSheet } from '../../../ui/sheet/YSheet';

export function MarkerModal({
  show,
  onHide,
  editing = false,
  copy = 'ground',
  name,
  setName,
  notes,
  setNotes,
  isStand,
  setIsStand,
  feed,
  setFeed,
  markerType,
  setMarkerType,
  winds = [],
  setWinds,
  feedingMode,
  setFeedingMode,
  feedCapacityLbs,
  setFeedCapacityLbs,
  consumptionRateCode,
  setConsumptionRateCode,
  refillThresholdPct,
  setRefillThresholdPct,
  canEnableFeed = true,
  onSave,
  onCancel,
  onMove,
  onDelete,
}) {
  const windList = Array.isArray(winds) ? winds : [];
  const lines = IDENTITY_TOGGLE_COPY[copy] || IDENTITY_TOGGLE_COPY.ground;
  const showMarkerType = copy !== 'club' && !isStand;
  const [showPro, setShowPro] = useState(false);

  useEffect(() => {
    if (!show) setShowPro(false);
  }, [show]);

  const handleFeedChange = (next) => {
    if (shouldGateFeedEnable(canEnableFeed, next)) {
      setShowPro(true);
      return;
    }
    setFeed(next);
    if (next) {
      if (!feedingMode && setFeedingMode) setFeedingMode('ground');
      if (!consumptionRateCode && setConsumptionRateCode) setConsumptionRateCode('avg');
    }
  };

  return (
    <YSheet
      show={show}
      onHide={onHide}
      kicker={editing ? 'EDIT' : 'NEW'}
      title={editing ? 'Edit waypoint' : 'New waypoint'}
      testId="waypoint-dialog"
      footerStart={editing && (typeof onMove === 'function' || typeof onDelete === 'function') ? (
        <>
          {typeof onMove === 'function' ? (
            <YButton
              type="button"
              variant="secondary"
              data-testid="waypoint-move"
              onClick={onMove}
            >
              Move on map
            </YButton>
          ) : null}
          {typeof onDelete === 'function' ? (
            <YButton
              type="button"
              variant="danger"
              data-testid="waypoint-delete"
              onClick={onDelete}
            >
              Delete
            </YButton>
          ) : null}
        </>
      ) : null}
      footer={
        <>
          <YButton type="button" variant="secondary" onClick={onCancel}>Cancel</YButton>
          <YButton type="button" data-testid="waypoint-save" onClick={onSave}>Save</YButton>
        </>
      }
    >
        <Form>
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="waypoint-stand"
              data-testid="waypoint-stand"
              label="Stand"
              checked={Boolean(isStand)}
              onChange={(e) => {
                const next = e.target.checked;
                setIsStand(next);
                if (!next && setWinds) setWinds([]);
              }}
            />
            <Form.Text className="text-muted">{lines.stand}</Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="waypoint-feed"
              data-testid="waypoint-feed"
              label="Feed site"
              checked={Boolean(feed)}
              onChange={(e) => handleFeedChange(e.target.checked)}
            />
            <Form.Text className="text-muted">{lines.feed}</Form.Text>
          </Form.Group>

          {showPro ? (
            <div className="mb-3">
              <ProPanel onDismiss={() => setShowPro(false)} />
            </div>
          ) : null}

          {isStand ? (
            <Form.Group className="mb-3" data-testid="waypoint-winds">
              <Form.Label>Optimal winds</Form.Label>
              <div className="d-flex flex-wrap" style={{ gap: 8 }}>
                {WIND_CODES.map((dir) => (
                  <Form.Check
                    key={dir}
                    inline
                    type="checkbox"
                    id={`waypoint-wind-${dir}`}
                    label={dir}
                    checked={windList.includes(dir)}
                    onChange={(e) => {
                      if (!setWinds) return;
                      if (e.target.checked) {
                        setWinds(Array.from(new Set([...windList, dir])));
                      } else {
                        setWinds(windList.filter((d) => d !== dir));
                      }
                    }}
                  />
                ))}
              </div>
            </Form.Group>
          ) : showMarkerType ? (
            <Form.Group className="mb-3">
              <Form.Label>Marker type</Form.Label>
              <Form.Select
                data-testid="waypoint-marker-type"
                value={markerTypeSelectValue(markerType)}
                onChange={(e) => setMarkerType(e.target.value)}
              >
                {POINT_MARKER_TYPES.map((option) => (
                  <option key={option.value || 'none'} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          ) : null}

          <Form.Group className="mb-3">
            <Form.Label>Name</Form.Label>
            <Form.Control
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name it"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes"
            />
          </Form.Group>

          {feed ? (
            <div data-testid="waypoint-feeding">
              <hr />
              <h6>Feeding</h6>
              <Form.Group className="mb-3">
                <Form.Label>Mode</Form.Label>
                <Form.Select
                  data-testid="waypoint-feed-mode"
                  value={feedingMode || 'ground'}
                  onChange={(e) => setFeedingMode && setFeedingMode(e.target.value || 'ground')}
                >
                  <option value="ground">Ground (no capacity)</option>
                  <option value="hopper">Hopper (capacity)</option>
                </Form.Select>
              </Form.Group>
              {feedingMode === 'hopper' && (
                <Form.Group className="mb-3">
                  <Form.Label>Capacity (lbs)</Form.Label>
                  <Form.Control
                    type="number"
                    min="1"
                    value={feedCapacityLbs || ''}
                    onChange={(e) => setFeedCapacityLbs && setFeedCapacityLbs(parseInt(e.target.value, 10) || null)}
                  />
                </Form.Group>
              )}
              <Form.Group className="mb-3">
                <Form.Label>Consumption rate</Form.Label>
                <Form.Select
                  value={consumptionRateCode || 'avg'}
                  onChange={(e) => setConsumptionRateCode && setConsumptionRateCode(e.target.value || 'avg')}
                >
                  <option value="low">Low (≈5 lbs/day)</option>
                  <option value="avg">Average (≈10 lbs/day)</option>
                  <option value="high">High (≈20 lbs/day)</option>
                  <option value="very_high">Very high (≈30 lbs/day)</option>
                </Form.Select>
              </Form.Group>
              {feedingMode === 'hopper' && (
                <Form.Group className="mb-3">
                  <Form.Label>Refill threshold (%)</Form.Label>
                  <Form.Control
                    type="number"
                    min="0"
                    max="100"
                    value={typeof refillThresholdPct === 'number' ? refillThresholdPct : ''}
                    onChange={(e) => setRefillThresholdPct && setRefillThresholdPct(parseInt(e.target.value, 10) || null)}
                  />
                </Form.Group>
              )}
            </div>
          ) : null}
        </Form>
    </YSheet>
  );
}
